import { useState, useCallback } from "react";
import type { PackingItemResponseDto } from "@tabipla/shared/types";
import { toggleSetItem } from "@/utils/packing-list";
import { useTogglePackingItem, useDeletePackingItem } from "./usePacking";
import { useErrorHandler } from "./useErrorHandler";

export interface UsePackingEditModeReturn {
  isEditMode: boolean;
  selectedItemIds: Set<string>;
  onToggleEditMode: () => void;
  onToggleItemSelection: (itemId: string) => void;
  onSelectAllItems: () => void;
  onDeselectAllItems: () => void;
  onBulkDelete: () => Promise<void>;
  onBulkToggleCheck: () => Promise<void>;
  isToggling: boolean;
  isDeleting: boolean;
}

/**
 * Hook for managing packing list edit mode state and bulk operations.
 */
export function usePackingEditMode(
  tripId: string,
  items: PackingItemResponseDto[]
): UsePackingEditModeReturn {
  const { handleError, showSuccess, t } = useErrorHandler();

  const toggleMutation = useTogglePackingItem(tripId);
  const deleteMutation = useDeletePackingItem(tripId);

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());

  const onToggleEditMode = useCallback(() => {
    setIsEditMode((prev) => !prev);
    setSelectedItemIds(new Set());
  }, []);

  const onToggleItemSelection = useCallback((itemId: string) => {
    setSelectedItemIds((prev) => toggleSetItem(prev, itemId));
  }, []);

  const onSelectAllItems = useCallback(() => {
    setSelectedItemIds(new Set(items.map((item) => item.id)));
  }, [items]);

  const onDeselectAllItems = useCallback(() => {
    setSelectedItemIds(new Set());
  }, []);

  const onBulkDelete = useCallback(async () => {
    if (selectedItemIds.size === 0) return;

    try {
      for (const itemId of selectedItemIds) {
        await deleteMutation.mutateAsync(itemId);
      }
      setSelectedItemIds(new Set());
      setIsEditMode(false);
      showSuccess(
        "packing.bulkDeleteSuccess",
        t("packing.bulkDeleteSuccess", {
          count: selectedItemIds.size,
          defaultValue: `${selectedItemIds.size}件を削除しました`,
        })
      );
    } catch (error) {
      handleError(error, { message: "一括削除に失敗しました" });
    }
  }, [selectedItemIds, deleteMutation, showSuccess, handleError, t]);

  const onBulkToggleCheck = useCallback(async () => {
    if (selectedItemIds.size === 0) return;

    try {
      for (const itemId of selectedItemIds) {
        await toggleMutation.mutateAsync(itemId);
      }
      setSelectedItemIds(new Set());
      showSuccess("packing.bulkToggleSuccess", "チェックを切り替えました");
    } catch (error) {
      handleError(error, { message: "チェックの切り替えに失敗しました" });
    }
  }, [selectedItemIds, toggleMutation, showSuccess, handleError]);

  return {
    isEditMode,
    selectedItemIds,
    onToggleEditMode,
    onToggleItemSelection,
    onSelectAllItems,
    onDeselectAllItems,
    onBulkDelete,
    onBulkToggleCheck,
    isToggling: toggleMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
